import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import "../../styles/common/popup.scss";
import "../../styles/common/evalManualPopup.scss";
import GlobalLoader from "./GlobalLoader";

const validateScore = (value) => {
  if (value === "" || value === null || typeof value === "undefined") {
    return "Score is required";
  }
  const num = Number(value);
  if (Number.isNaN(num)) return "Enter a valid number";
  if (num < 0) return "You cant enter below 0";
  if (num > 10) return "You cant enter above 10";
  return "";
};

const EvalManualPopup = ({
  open,
  onClose,
  onSave,
  initialScores,
  saving = false,
}) => {
  const initial = useMemo(
    () => ({
      quality_score: initialScores?.quality_score ?? 0,
      relevance_score: initialScores?.relevance_score ?? 0,
      grounding_score: initialScores?.grounding_score ?? 0,
      reasoning_score: initialScores?.reasoning_score ?? 0,
      risk_score: initialScores?.risk_score ?? 0,
    }),
    [initialScores],
  );

  const [scores, setScores] = useState(initial);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setScores(initial);
    setErrors({});
  }, [open, initial]);

  if (!open) return null;

  const handleChange = (field) => (e) => {
    const next = e.target.value;
    setScores((prev) => ({ ...prev, [field]: next }));
    const err = validateScore(next);
    setErrors((prev) => ({ ...prev, [field]: err }));
  };

  const handleCancel = () => {
    if (saving) return;
    onClose && onClose();
  };

  const handleSave = async () => {
    if (saving) return;
    const nextErrors = {
      quality_score: validateScore(scores.quality_score),
      relevance_score: validateScore(scores.relevance_score),
      grounding_score: validateScore(scores.grounding_score),
      reasoning_score: validateScore(scores.reasoning_score),
      risk_score: validateScore(scores.risk_score),
    };
    setErrors(nextErrors);
    const hasError = Object.values(nextErrors).some((e) => !!e);
    if (hasError) return;
    if (onSave) {
      await onSave({
        quality_score: Number(scores.quality_score),
        relevance_score: Number(scores.relevance_score),
        grounding_score: Number(scores.grounding_score),
        reasoning_score: Number(scores.reasoning_score),
        risk_score: Number(scores.risk_score),
      });
    }
  };

  const isSaveDisabled =
    saving ||
    Object.values(errors).some((e) => !!e) ||
    [
      scores.quality_score,
      scores.relevance_score,
      scores.grounding_score,
      scores.reasoning_score,
      scores.risk_score,
    ].some((v) => v === "" || typeof v === "undefined" || v === null);

  return createPortal(
    <>
      <GlobalLoader visible={saving} text="Loading..." />
      <div
        className="common-popup-overlay high-z-index"
        role="dialog"
        aria-modal="true"
        onClick={handleCancel}
      >
        <div
          className="common-popup-container eval-manual-popup-container"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="popup-header with-close">
            <h2>Evaluate Manually</h2>
            <button
              type="button"
              className="icon-button"
              onClick={handleCancel}
              aria-label="Close"
              disabled={saving}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>

          <div className="popup-content eval-manual-content">
            <div className="eval-manual-form">
              <div className="eval-manual-row">
                <label>Quality score</label>
                <div className="eval-manual-input">
                  <input
                  type="number"
                  min={0}
                  max={10}
                  value={scores.quality_score}
                  onChange={handleChange("quality_score")}
                />
                  <div className="eval-manual-error" aria-live="polite">
                    {errors.quality_score || ""}
                  </div>
                </div>
              </div>
              <div className="eval-manual-row">
                <label>Relevance score</label>
                <div className="eval-manual-input">
                  <input
                  type="number"
                  min={0}
                  max={10}
                  value={scores.relevance_score}
                  onChange={handleChange("relevance_score")}
                />
                  <div className="eval-manual-error" aria-live="polite">
                    {errors.relevance_score || ""}
                  </div>
                </div>
              </div>
              <div className="eval-manual-row">
                <label>Grounding score</label>
                <div className="eval-manual-input">
                  <input
                  type="number"
                  min={0}
                  max={10}
                  value={scores.grounding_score}
                  onChange={handleChange("grounding_score")}
                />
                  <div className="eval-manual-error" aria-live="polite">
                    {errors.grounding_score || ""}
                  </div>
                </div>
              </div>
              <div className="eval-manual-row">
                <label>Reasoning score</label>
                <div className="eval-manual-input">
                  <input
                  type="number"
                  min={0}
                  max={10}
                  value={scores.reasoning_score}
                  onChange={handleChange("reasoning_score")}
                />
                  <div className="eval-manual-error" aria-live="polite">
                    {errors.reasoning_score || ""}
                  </div>
                </div>
              </div>
              <div className="eval-manual-row">
                <label>Risk score</label>
                <div className="eval-manual-input">
                  <input
                  type="number"
                  min={0}
                  max={10}
                  value={scores.risk_score}
                  onChange={handleChange("risk_score")}
                />
                  <div className="eval-manual-error" aria-live="polite">
                    {errors.risk_score || ""}
                  </div>
                </div>
              </div>
            </div>
            <div className="popup-actions">
              <button
                type="button"
                className="popup-submit-button"
                onClick={handleSave}
                disabled={isSaveDisabled}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default EvalManualPopup;
